import React, { useCallback } from "react";
import { Entity } from "@common/types/entity";
import { pluralize } from "@common/helpers/string";
import { IconWithTooltip } from "@components/general/IconList/IconWithTooltip";
import {
    IconList,
    RenderAggregated,
    RenderItem,
} from "@components/general/IconList/IconList";
import { EntityIcon } from "@components/entity/EntityIcon";
import {
    IconSize,
    ObjectIcon,
} from "@components/general/ObjectIcon/ObjectIcon";

interface EntityIconListProps {
    entities: Entity[];
    maxEntitiesToDisplay?: number;
    label?: string;
    showLabel?: boolean;
    iconClassName?: string;
    iconSize?: IconSize;
}

export const EntityIconList: React.FC<EntityIconListProps> = ({
    entities,
    maxEntitiesToDisplay = 4,
    label,
    showLabel = true,
    iconClassName,
    iconSize = "xs",
}) => {
    const displayedLabel = showLabel ? (
        <span className="ml-2">
            {label ?? (
                <>
                    {entities.length} {pluralize("Entity", entities.length)}
                </>
            )}
        </span>
    ) : null;

    const renderEntityIcon: RenderItem<Entity> = useCallback(
        (entity) => (
            <IconWithTooltip
                key={entity.id}
                tooltipId={`filter-entity-${entity.id}`}
                tooltip={entity.name}
                className={iconClassName}
            >
                <EntityIcon entity={entity} size={iconSize} />
            </IconWithTooltip>
        ),
        [iconClassName, iconSize],
    );

    const renderAggregatedIcon: RenderAggregated<Entity> = useCallback(
        (otherEntities, { aggregatedCount }) => (
            <IconWithTooltip
                tooltipId={`filter-entity-more`}
                tooltip={otherEntities.map((entity) => (
                    <React.Fragment key={entity.id}>
                        {entity.name} <br />
                    </React.Fragment>
                ))}
                className={iconClassName}
            >
                <ObjectIcon
                    defaultColor="#D9DDE2"
                    size={iconSize}
                    square
                    dynamic
                >
                    +{aggregatedCount}
                </ObjectIcon>
            </IconWithTooltip>
        ),
        [iconClassName, iconSize],
    );

    return (
        <IconList
            items={entities}
            renderItem={renderEntityIcon}
            renderAggregated={renderAggregatedIcon}
            maxItems={maxEntitiesToDisplay}
            appendContent={displayedLabel}
        />
    );
};
